// Composer disclosure state (FR-27 v11.8; v70 index.html:3462-3473): whether
// the composer is expanded, and whether its Add-reward panel is open.
// context: `expand` fires on the root's onFocus (v70 focusin) and onMouseDown
//   (a re-click on an already-focused message raises no focus event).
//   Collapse fires on a document mousedown+mouseup outside the root and the
//   portalled offer listbox (a bare root.contains() re-creates pin #1155),
//   only while `canCollapse`, deferred to mouseup (see below). Once dirty,
//   `expanded` latches true so clearing the draft mid-edit can't re-collapse.
//   Both reset on every item switch (v70 openItem: `collapsed` + resetOffer).
import {
  Dispatch,
  RefObject,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react'
import { OFFER_LISTBOX_ID } from './OfferPicker'

// A DOM check, not a React-tree flag: React 16 delegates at `document`, so a
// flag would depend on listener order. Name any new composer-owned portal here.
export function isInsideComposer(
  root: HTMLElement | null,
  target: EventTarget | null
): boolean {
  if (!(target instanceof Element)) return false
  return (
    !!root?.contains(target) || target.closest(`#${OFFER_LISTBOX_ID}`) !== null
  )
}

export interface UseComposerDisclosureOptions {
  itemId: string
  rootRef: RefObject<HTMLElement>
  // False while the draft holds anything unsent (`isDirty`).
  canCollapse: boolean
}

export interface UseComposerDisclosureResult {
  expanded: boolean
  expand: () => void
  // v70: the Offer/Points radio + picker sits behind the "Add reward" toggle.
  rewardOpen: boolean
  setRewardOpen: Dispatch<SetStateAction<boolean>>
}

export function useComposerDisclosure({
  itemId,
  rootRef,
  canCollapse,
}: UseComposerDisclosureOptions): UseComposerDisclosureResult {
  const [expanded, setExpanded] = useState(false)
  const [rewardOpen, setRewardOpen] = useState(false)

  useEffect(() => {
    setExpanded(false)
    setRewardOpen(false)
  }, [itemId])

  // Once the draft goes dirty, stay expanded even if it's cleared back out
  // mid-edit (e.g. typing then deleting a character) — otherwise a draft
  // that transiently touched `isDirty` would flicker collapsed the instant
  // it's empty again, since `expanded` itself was never set.
  useEffect(() => {
    if (!canCollapse) setExpanded(true)
  }, [canCollapse])

  useEffect(() => {
    if (!expanded || !canCollapse) return
    // Capture phase (matches OfferPicker's own outside listener) so a
    // descendant's stopPropagation can't hide the mousedown.
    // context: collapse itself waits for the paired mouseup rather than
    //   firing inline on mousedown. This listener runs outside React's
    //   batching, so setting state on mousedown reflows the composer
    //   synchronously mid-gesture; if that shifts content under the pointer
    //   before mouseup, the click on the actual outside target gets lost.
    //   The mouseup also re-checks focus: a mousedown on a non-focusable
    //   outside element (e.g. a scrollbar) never moves focus away, so an
    //   editing control (Subject/Add reward/Send) can still be focused at
    //   collapse time. Collapsing there would unmount it and drop focus to
    //   `<body>` — skip the collapse instead of stealing focus.
    let pendingCollapse = false
    const handleMouseDown = (event: MouseEvent) => {
      pendingCollapse = !isInsideComposer(rootRef.current, event.target)
    }
    const handleMouseUp = () => {
      if (
        pendingCollapse &&
        !isInsideComposer(rootRef.current, document.activeElement)
      ) {
        setExpanded(false)
      }
      pendingCollapse = false
    }
    document.addEventListener('mousedown', handleMouseDown, true)
    document.addEventListener('mouseup', handleMouseUp, true)
    return () => {
      document.removeEventListener('mousedown', handleMouseDown, true)
      document.removeEventListener('mouseup', handleMouseUp, true)
    }
  }, [expanded, canCollapse, rootRef])

  const expand = useCallback(() => setExpanded(true), [])

  return { expanded, expand, rewardOpen, setRewardOpen }
}
